import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PageItem, TextBox } from '../lib/types';
import { renderPreview } from '../lib/thumbnails';
import { track } from '../lib/usage';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MinusIcon,
  PlusIcon,
  RotateLeftIcon,
  RotateRightIcon,
  XIcon,
} from './Icons';

/** Zoom steps, in multiples of the rendered page. 'fit' scales it to the stage. */
const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3] as const;

interface PagePreviewProps {
  page: PageItem;
  /** Replaces this page's text boxes. */
  onTextsChange: (uid: string, texts: NonNullable<PageItem['texts']>) => void;
  /** Position in the working canvas, 1-based. */
  position: number;
  totalPages: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onRotate: (uid: string, delta: number) => void;
}

/**
 * A full-size look at one page.
 *
 * Paging left and right steps through the canvas in its current order rather
 * than the source document, since that is the order being assembled — checking
 * it is usually the reason for opening this at all. Rotation is available here
 * too, because spotting a sideways page is the other reason.
 */
export function PagePreview({
  page,
  position,
  totalPages,
  onClose,
  onPrevious,
  onNext,
  onRotate,
  onTextsChange,
}: PagePreviewProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  /*
   * 'fit' scales the page down to the stage, which is right for glancing at
   * layout and useless for reading body text. A zoom level switches to the
   * rendered size, and the stage scrolls.
   */
  const [zoom, setZoom] = useState<'fit' | number>('fit');
  /** While on, clicking the page drops a new text box where you click. */
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  /*
   * The box the style controls act on. It has to outlive `editing`, because
   * clicking any control blurs the textarea and closes the editor before the
   * control's own change fires — so keying the controls to `editing` meant a
   * box could only ever be styled before it was placed, never after.
   */
  const [selected, setSelected] = useState<string | null>(null);
  /*
   * Style for the next box, and for the one being edited. Keeping one set of
   * controls means they read as "the style" rather than two sets that disagree.
   */
  const [style, setStyle] = useState<{
    size: number;
    angle: number;
    font: 'helvetica' | 'times' | 'courier';
    bold: boolean;
    italic: boolean;
    color: string;
  }>({ size: 14, angle: 0, font: 'helvetica', bold: false, italic: false, color: '#000000' });
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  /** The page's own size in points, needed to show text at its true size. */
  const [pageSizePt, setPageSizePt] = useState<{ width: number; height: number } | null>(null);
  /*
   * A move in progress. The position lives here rather than in the canvas so
   * that dragging a box across the page is one undo step, not one per pixel.
   */
  const [moving, setMoving] = useState<{ id: string; x: number; y: number } | null>(null);
  const dragRef = useRef<{
    id: string;
    pointerId: number;
    frame: HTMLElement;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    moved: boolean;
  } | null>(null);
  /** Set when a drag ends, so the click that follows it does not open the editor. */
  const draggedRef = useRef(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState<{ width: number; height: number } | null>(null);

  // The fit scale depends on the stage, so it has to be measured — and
  // remeasured when the window changes.
  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setStageSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setFailed(false);

    /*
     * A blank page has no source to render, so it must never reach
     * renderPreview — that is what produced "could not be rendered" when one
     * was opened, or simply paged onto with an arrow key. Sizing it like a
     * rendered Letter page keeps the fit and zoom arithmetic below identical;
     * its real size is decided at export from the pages around it.
     */
    if (page.blank) {
      setNatural({ width: 1224, height: 1584 });
      setPageSizePt({ width: 612, height: 792 });
      return;
    }

    setNatural(null);
    renderPreview(page.docId, page.pageIndex)
      .then((render) => {
        if (cancelled) return;
        setSrc(render.url);
        setPageSizePt({ width: render.widthPt, height: render.heightPt });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [page.blank, page.docId, page.pageIndex]);

  // Paging on leaves the old box selected otherwise, and the controls would
  // then be editing something off screen.
  useEffect(() => {
    setSelected(null);
    setEditing(null);
  }, [page.uid]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      /*
       * Backspace in the size or angle field is someone correcting a number,
       * not asking for the box to go. Typing anywhere takes precedence over
       * these shortcuts, which is also what keeps [ and ] out of the text.
       */
      const node = document.activeElement;
      const typing =
        node instanceof HTMLInputElement ||
        node instanceof HTMLTextAreaElement ||
        node instanceof HTMLSelectElement ||
        (node instanceof HTMLElement && node.isContentEditable);
      if (typing && event.key !== 'Escape') return;

      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowLeft') onPrevious();
      else if (event.key === 'ArrowRight') onNext();
      else if (event.key === '[') onRotate(page.uid, -90);
      else if (event.key === ']') onRotate(page.uid, 90);
      else if (event.key === '+' || event.key === '=') zoomBy(1);
      else if (event.key === '-') zoomBy(-1);
      else if (event.key === '0') setZoom('fit');
      else if ((event.key === 'Delete' || event.key === 'Backspace') && selected && !editing) {
        // Written out rather than calling removeText: this listener is bound
        // once per change of its deps, and a helper redefined every render
        // would be captured stale.
        setSelected(null);
        onTextsChange(
          page.uid,
          (page.texts ?? []).filter((t) => t.id !== selected),
        );
      }
      else return;
      event.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrevious, onNext, onRotate, onTextsChange, page.uid, page.texts, selected, editing]);

  function zoomBy(direction: number) {
    setZoom((current) => {
      if (current === 'fit') return direction > 0 ? ZOOM_STEPS[2]! : ZOOM_STEPS[0]!;
      const index = ZOOM_STEPS.indexOf(current as (typeof ZOOM_STEPS)[number]);
      const next = Math.min(ZOOM_STEPS.length - 1, Math.max(0, index + direction));
      return ZOOM_STEPS[next]!;
    });
  }

  const texts = page.texts ?? [];


  function updateTexts(next: NonNullable<PageItem['texts']>) {
    onTextsChange(page.uid, next);
  }

  /** Applies a style change to the box being edited, and to new ones after it. */
  function restyle(next: Partial<typeof style>) {
    setStyle((current) => ({ ...current, ...next }));
    if (selected) {
      updateTexts(texts.map((t) => (t.id === selected ? { ...t, ...next } : t)));
    }
  }

  /** Finishes an edit, discarding a box nobody typed into. */
  function commitEditing() {
    if (!editing) return;
    const box = texts.find((t) => t.id === editing);
    setEditing(null);
    if (box && !box.text.trim()) updateTexts(texts.filter((t) => t.id !== editing));
  }

  /** Keeps a box's anchor on the page, matching where a click may place one. */
  function clampFraction(value: number) {
    return Math.min(0.98, Math.max(0, value));
  }

  /*
   * Opening a box loads its own style into the controls. They are shared, so
   * without this the angle and size fields would describe the last box made
   * rather than the one on screen — and a nudge of any single control would
   * read as the others changing too.
   */
  function selectBox(box: TextBox) {
    setStyle((current) => ({
      ...current,
      size: box.size,
      angle: box.angle ?? 0,
      font: box.font ?? 'helvetica',
      bold: box.bold ?? false,
      italic: box.italic ?? false,
      color: box.color ?? '#000000',
    }));
    setSelected(box.id);
  }

  function beginEditing(box: TextBox) {
    selectBox(box);
    setEditing(box.id);
  }

  function removeText(id: string) {
    if (selected === id) setSelected(null);
    if (editing === id) setEditing(null);
    updateTexts(texts.filter((t) => t.id !== id));
  }

  function startMove(event: React.PointerEvent<HTMLDivElement>, box: TextBox) {
    // While editing, the pointer belongs to the textarea for selecting text.
    if (editing === box.id || event.button !== 0) return;
    const frame = event.currentTarget.parentElement;
    if (!frame) return;
    event.stopPropagation();
    const rect = frame.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    // Grabbing a box is also a way of choosing the one the controls act on.
    selectBox(box);
    dragRef.current = {
      id: box.id,
      pointerId: event.pointerId,
      frame,
      startX: event.clientX,
      startY: event.clientY,
      // Grabbing the middle of a box must not snap its corner to the pointer.
      offsetX: (event.clientX - rect.left) / rect.width - box.x,
      offsetY: (event.clientY - rect.top) / rect.height - box.y,
      moved: false,
    };
  }

  function trackMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.moved) {
      // The wobble of an ordinary click is not a drag; opening the editor is.
      const far =
        Math.abs(event.clientX - drag.startX) > 3 || Math.abs(event.clientY - drag.startY) > 3;
      if (!far) return;
      drag.moved = true;
    }
    // Remeasured each time: the stage can scroll under a long drag.
    const rect = drag.frame.getBoundingClientRect();
    setMoving({
      id: drag.id,
      x: clampFraction((event.clientX - rect.left) / rect.width - drag.offsetX),
      y: clampFraction((event.clientY - rect.top) / rect.height - drag.offsetY),
    });
  }

  function endMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    const landed = moving;
    setMoving(null);
    if (!drag.moved || !landed || landed.id !== drag.id) return;
    draggedRef.current = true;
    updateTexts(texts.map((t) => (t.id === drag.id ? { ...t, x: landed.x, y: landed.y } : t)));
  }

  /** Where the click landed, as a fraction of the page the reader sees. */
  function placeAt(event: React.MouseEvent<HTMLDivElement>) {
    if (!adding) {
      // Clicking the page away from a box finishes editing it — relying on the
      // textarea's blur alone leaves the editor open in some cases.
      commitEditing();
      setSelected(null);
      return;
    }
    const frame = event.currentTarget.getBoundingClientRect();
    const id = crypto.randomUUID();
    updateTexts([
      ...texts,
      {
        id,
        text: '',
        x: clampFraction((event.clientX - frame.left) / frame.width),
        y: clampFraction((event.clientY - frame.top) / frame.height),
        ...style,
      },
    ]);
    setAdding(false);
    setSelected(id);
    setEditing(id);
    track('add_text');
  }

  const quarterTurned = page.rotation % 180 === 90;

  /*
   * Sizing is computed rather than left to CSS.
   *
   * A rotated page's layout box is unrotated — the turn is visual only — so
   * "fit" means constraining the box by the OPPOSITE axis, which percentages
   * and viewport units cannot express against the stage. Getting this wrong is
   * how a quarter-turned page ended up overflowing a stage it was supposed to
   * fit inside.
   */
  const visual = natural
    ? {
        width: quarterTurned ? natural.height : natural.width,
        height: quarterTurned ? natural.width : natural.height,
      }
    : null;

  const fitScale =
    visual && stageSize && visual.width > 0 && visual.height > 0
      ? Math.min(stageSize.width / visual.width, stageSize.height / visual.height, 1)
      : 1;

  const scale = zoom === 'fit' ? fitScale : zoom;
  const zoomed = zoom !== 'fit';

  // The frame carries the rotated extents, so the stage has something real to
  // scroll; the image keeps its own unrotated dimensions.
  const frameStyle = visual
    ? { width: visual.width * scale, height: visual.height * scale }
    : undefined;
  /*
   * On-screen pixels per PDF point. The preview is a render of the page at one
   * scale, shown on screen at another, so both are needed — using only the fit
   * scale drew 14pt text at about 6px.
   */
  const displayedPagePt = pageSizePt
    ? quarterTurned
      ? { width: pageSizePt.height, height: pageSizePt.width }
      : pageSizePt
    : null;
  const pxPerPoint =
    displayedPagePt && visual ? (visual.width * scale) / displayedPagePt.width : scale;

  const imageStyle = natural
    ? {
        width: natural.width * scale,
        height: natural.height * scale,
        maxWidth: 'none' as const,
        maxHeight: 'none' as const,
      }
    : {};

  return (
    <div
      className="preview"
      role="dialog"
      aria-modal="true"
      aria-label={`Page ${position} of ${totalPages}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className="preview__bar">
        <div className="preview__label">
          <strong>Page {position}</strong> of {totalPages}
          <span className="preview__source" title={page.docName}>
            {page.blank ? 'Blank page' : `${page.docName} · page ${page.pageIndex + 1}`}
          </span>
        </div>
        <button
          className={adding ? 'btn btn--sm btn--active' : 'btn btn--sm'}
          onClick={() => setAdding((v) => !v)}
          disabled={page.blank}
          title="Click the page to place text. Drag a box to move it."
        >
          {adding ? 'Click the page…' : 'Add text'}
        </button>
        <select
          className="preview__font"
          value={style.font}
          onChange={(event) => restyle({ font: event.target.value as typeof style.font })}
          title="Font"
        >
          <option value="helvetica">Helvetica</option>
          <option value="times">Times</option>
          <option value="courier">Courier</option>
        </select>
        <label className="preview__size" title="Text size">
          <input
            type="number"
            min={6}
            max={96}
            value={style.size}
            onChange={(event) => restyle({ size: Number(event.target.value) || 14 })}
          />
          pt
        </label>
        <label className="preview__size" title="Turn the text (degrees clockwise)">
          <input
            type="number"
            min={-180}
            max={180}
            step={15}
            value={style.angle}
            onChange={(event) => restyle({ angle: Number(event.target.value) || 0 })}
          />
          °
        </label>
        <button
          className={style.bold ? 'icon-btn is-on' : 'icon-btn'}
          onClick={() => restyle({ bold: !style.bold })}
          title="Bold"
          style={{ fontWeight: 700 }}
        >
          B
        </button>
        <button
          className={style.italic ? 'icon-btn is-on' : 'icon-btn'}
          onClick={() => restyle({ italic: !style.italic })}
          title="Italic"
          style={{ fontStyle: 'italic', fontFamily: 'Georgia, serif' }}
        >
          I
        </button>
        <input
          type="color"
          className="preview__color"
          value={style.color}
          onChange={(event) => restyle({ color: event.target.value })}
          title="Text color"
        />
        <span className="preview__divider" />
        <button
          className="icon-btn"
          onClick={() => zoomBy(-1)}
          title="Zoom out (−)"
          aria-label="Zoom out"
        >
          <MinusIcon />
        </button>
        <button
          className="preview__zoom"
          onClick={() => setZoom((c) => (c === 'fit' ? 1 : 'fit'))}
          title="Fit to window (0)"
        >
          {zoom === 'fit' ? 'Fit' : `${Math.round(zoom * 100)}%`}
        </button>
        <button
          className="icon-btn"
          onClick={() => zoomBy(1)}
          title="Zoom in (+)"
          aria-label="Zoom in"
        >
          <PlusIcon />
        </button>
        <span className="preview__divider" />
        <button
          className="icon-btn"
          onClick={() => onRotate(page.uid, -90)}
          title="Rotate left ([)"
          disabled={page.blank}
        >
          <RotateLeftIcon />
        </button>
        <button
          className="icon-btn"
          onClick={() => onRotate(page.uid, 90)}
          title="Rotate right (])"
          disabled={page.blank}
        >
          <RotateRightIcon />
        </button>
        <button className="icon-btn" onClick={onClose} title="Close (Esc)" aria-label="Close preview">
          <XIcon />
        </button>
      </header>

      {texts.length > 0 ? (
        // Neither moving nor removing is visible until you try it, so say so.
        <p className="preview__hint">Drag text to move it. Hover over it and click × to remove it.</p>
      ) : null}

      <button
        className="preview__nav preview__nav--prev"
        onClick={onPrevious}
        disabled={totalPages < 2}
        aria-label="Previous page"
        title="Previous page (←)"
      >
        <ChevronLeftIcon />
      </button>

      <div
        ref={stageRef}
        className={zoomed ? 'preview__stage preview__stage--zoomed' : 'preview__stage'}
      >
        {page.blank ? (
          <div className="preview__frame" style={frameStyle}>
            <div className="preview__blank" style={imageStyle} aria-label="Blank page" />
          </div>
        ) : src ? (
          <div
            className={adding ? 'preview__frame is-placing' : 'preview__frame'}
            style={frameStyle}
            onClick={placeAt}
          >
            {texts.map((box) => (
              <div
                key={box.id}
                className={[
                  'preview__text',
                  editing === box.id ? 'preview__text--editing' : '',
                  selected === box.id ? 'preview__text--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onPointerDown={(event) => startMove(event, box)}
                onPointerMove={trackMove}
                onPointerUp={endMove}
                onPointerCancel={endMove}
                onClick={(event) => {
                  event.stopPropagation();
                  // The click that ends a drag is not a request to edit.
                  if (draggedRef.current) {
                    draggedRef.current = false;
                    return;
                  }
                  if (editing !== box.id) beginEditing(box);
                }}
                style={{
                  left: `${(moving?.id === box.id ? moving.x : box.x) * 100}%`,
                  top: `${(moving?.id === box.id ? moving.y : box.y) * 100}%`,
                  // The preview is a scaled render, so the on-screen size has
                  // to scale with it or the box would lie about the result.
                  fontSize: `${box.size * pxPerPoint}px`,
                  fontFamily:
                    box.font === 'times'
                      ? 'Times, "Times New Roman", serif'
                      : box.font === 'courier'
                        ? 'Courier, "Courier New", monospace'
                        : 'Helvetica, Arial, sans-serif',
                  fontWeight: box.bold ? 700 : 400,
                  fontStyle: box.italic ? 'italic' : 'normal',
                  color: box.color ?? '#000000',
                  // Turned about the anchor, which is what the export measures
                  // from; the nudge comes after so it follows the turn.
                  transform: `rotate(${box.angle ?? 0}deg) translateY(-0.1em)`,
                }}
              >
                {editing === box.id ? (
                  <textarea
                    autoFocus
                    value={box.text}
                    onChange={(event) =>
                      updateTexts(
                        texts.map((t) => (t.id === box.id ? { ...t, text: event.target.value } : t)),
                      )
                    }
                    onBlur={commitEditing}
                    onKeyDown={(event) => {
                      // Enter commits; Shift+Enter is a new line. Escape leaves
                      // the box as it was rather than closing the whole preview.
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        commitEditing();
                      } else if (event.key === 'Escape') {
                        event.stopPropagation();
                        commitEditing();
                      }
                    }}
                    onClick={(event) => event.stopPropagation()}
                    rows={1}
                  />
                ) : (
                  <span>{box.text}</span>
                )}
                <button
                  type="button"
                  className="preview__text-remove"
                  title="Remove this text"
                  aria-label="Remove this text"
                  // Keeping focus means the textarea does not blur first and
                  // leave this button re-rendered out from under the click.
                  onMouseDown={(event) => event.preventDefault()}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeText(box.id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            <img
              src={src}
              alt={`Page ${page.pageIndex + 1} of ${page.docName}`}
              style={{
                // Centered by translate rather than by layout: the image is
                // absolutely positioned so its unrotated box cannot push the
                // frame around, which is what made a quarter-turned page
                // overflow a stage it was sized to fit.
                transform: `translate(-50%, -50%) rotate(${page.rotation}deg)`,
                ...imageStyle,
              }}
              onLoad={(event) =>
                setNatural({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
              onDoubleClick={() => setZoom((c) => (c === 'fit' ? 1 : 'fit'))}
              draggable={false}
            />
          </div>
        ) : (
          <p className="preview__status">{failed ? 'This page could not be rendered.' : 'Loading…'}</p>
        )}
      </div>

      <button
        className="preview__nav preview__nav--next"
        onClick={onNext}
        disabled={totalPages < 2}
        aria-label="Next page"
        title="Next page (→)"
      >
        <ChevronRightIcon />
      </button>
    </div>
  );
}
